/**
 * ElevationProfileV2 Component
 * SVG elevation chart with red line + fill on dark background
 * Aid station markers with segment runner counts
 * Runner position pin for detail view
 *
 * Data: pre-derived `ElevationProfile` (see shared/src/elevation.ts). The raw
 * ~2.1 MB GPX/TSV export is parsed ONCE at upload time (Server Action / admin
 * backend / ETL importer, not here) into an ~8 KB JSON blob of track points
 * and waypoints. This component used to do that parsing itself on every page
 * load — see git history before this change for the ~100-line parser that
 * used to live here.
 */

import { useMemo } from 'react';
import type { ElevationProfile } from '@as-checkin/shared';
import type { AidStation, RunnerSummary } from '../../lib/types';
import { StationBubble } from './StationBubble';
import { buildStationTrackDistances, finishTrackDistance } from '../../lib/stationWaypointMatch';
import { isHiddenParentStation } from '../../lib/hiddenParentStation';

interface ElevationProfileV2Props {
  data: ElevationProfile | null;
  stations?: AidStation[];
  runners?: RunnerSummary[];
  /** Runner's current distance for position pin */
  runnerDistance?: number;
  /** Mini version for runner detail page */
  mini?: boolean;
  onStationClick?: (stationIndex: number) => void;
  onRunnerClick?: (runnerId: string) => void;
  /** Followed runner bibs for the bubble popover star button (no-op in mini mode) */
  followedBibs?: Set<string>;
  onToggleFollow?: (bib: string) => void;
}

interface StationRunnerSplit {
  atStation: RunnerSummary[];
  enrouteTo: RunnerSummary[];
}

/**
 * Group runners per station into two phases: physically at the station
 * (checked in but not yet checked out) vs en route to the station (last
 * checkpoint was the prior station, not yet arrived).
 *
 * Source of truth: `runner.phase` ('at_station' | 'enroute') and
 * `runner.next_checkpoint_id` / `next_checkpoint`, both populated upstream
 * from the shared `calculateRunnerStatus()` library.
 */
function getStationRunnerSplit(
  stations: AidStation[],
  runners: RunnerSummary[]
): Map<string, StationRunnerSplit> {
  const split = new Map<string, StationRunnerSplit>();
  for (const station of stations) {
    split.set(station.id, { atStation: [], enrouteTo: [] });
  }

  // Build name fallback map for runners missing next_checkpoint_id
  const stationByName = new Map<string, AidStation>();
  for (const s of stations) stationByName.set(s.name, s);

  for (const runner of runners) {
    if (runner.status !== 'on_course' || runner.phase == null) continue;

    let stationId = runner.next_checkpoint_id;
    if (!stationId && runner.next_checkpoint) {
      stationId = stationByName.get(runner.next_checkpoint)?.id ?? null;
    }
    if (!stationId) continue;

    const bucket = split.get(stationId);
    if (!bucket) continue;

    if (runner.phase === 'at_station') bucket.atStation.push(runner);
    else if (runner.phase === 'enroute') bucket.enrouteTo.push(runner);
  }
  return split;
}

export function ElevationProfileV2({
  data,
  stations = [],
  runners = [],
  runnerDistance,
  mini = false,
  onStationClick,
  followedBibs,
  onToggleFollow,
}: ElevationProfileV2Props) {
  const svgHeight = mini ? 80 : 160;
  const svgPadding = { top: 10, right: 20, bottom: mini ? 10 : 30, left: 20 };

  // The stored profile uses compact keys (d/a) to keep the JSON small. The
  // ~250 lines of chart geometry below have always used descriptive names, so
  // rename once here rather than editing every reference — that keeps this
  // change surgical and leaves the drawing code provably untouched.
  const adapted = useMemo(
    () =>
      data
        ? {
            trackPoints: data.points.map(p => ({ distance: p.d, altitude: p.a })),
            waypoints: data.waypoints.map(w => ({ name: w.name, distance: w.d })),
          }
        : null,
    [data],
  );

  const stationSplit = useMemo(
    () => getStationRunnerSplit(stations, runners),
    [stations, runners]
  );

  // Build a map from station ID to track-derived distance (from the profile's
  // waypoints). Haversine-computed distances between GPS points underestimate
  // actual trail distance (missing switchbacks, elevation changes), so official
  // station mileages don't align with the elevation line. By matching stations to
  // waypoints, dots sit on the correct part of the elevation profile.
  //
  // AD-224: matching is done by the waypoint's embedded official mile
  // ("M 58.6 - ...") first, falling back to name-contains — see
  // buildStationTrackDistances. The old name-only match failed for stations whose
  // DB name differs from the decorated CalTopo waypoint name (e.g. "Our Lady of
  // the Rockies" vs "M 58.6 - no-Crew: Our Lady"), dropping them onto their
  // official mileage on a track-distance axis at the wrong x.
  const stationTrackDistances = useMemo(() => {
    if (!data) return new Map<string, number>();
    return buildStationTrackDistances(
      stations.map(s => ({ id: s.id, name: s.name, mileage: s.mileage })),
      data.waypoints,
    );
  }, [data, stations]);

  const chartData = useMemo(() => {
    if (!adapted || adapted.trackPoints.length === 0) return null;

    const points = adapted.trackPoints;
    const trackMaxDist = points[points.length - 1]!.distance;

    // The track data may end before the actual finish line (e.g., track ends at
    // Moulton ~90mi but finish is at 100.1mi), so the X-axis may need to extend
    // to the finish. finishTrackDistance converts the finish's official mileage
    // into track units using the furthest PLAUSIBLE waypoint/station pairing.
    // (The old version paired the last waypoint by name alone: on a loop course
    // like Deception Pass, "Cornet Aid Station" on the 2nd pass matched the
    // 1st-pass "Cornet", stretched the axis 1.44x, and left a long flat line.)
    const finishStation = stations.find(s => s.check_mode === 'finish');
    const maxDist = finishTrackDistance(
      trackMaxDist,
      finishStation?.mileage,
      stations.map(s => ({ id: s.id, name: s.name, mileage: s.mileage })),
      data?.waypoints ?? [],
    );

    const minAlt = Math.min(...points.map(p => p.altitude));
    const maxAlt = Math.max(...points.map(p => p.altitude));
    const altRange = maxAlt - minAlt || 1;

    return { points, maxDist, minAlt, maxAlt, altRange };
  }, [adapted, stations, data]);

  if (!chartData) {
    return (
      <div className={`v2-elevation ${mini ? 'v2-elevation-mini' : ''}`}>
        <div style={{ height: svgHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'var(--v2-text-muted)', fontSize: 12 }}>
            No elevation data available
          </span>
        </div>
      </div>
    );
  }

  const { points, maxDist, minAlt, altRange } = chartData;
  const viewBoxWidth = 1000;
  const chartWidth = viewBoxWidth - svgPadding.left - svgPadding.right;
  const chartHeight = svgHeight - svgPadding.top - svgPadding.bottom;

  const toX = (dist: number) => svgPadding.left + (dist / maxDist) * chartWidth;
  const toY = (alt: number) => svgPadding.top + chartHeight - ((alt - minAlt) / altRange) * chartHeight;

  // Build SVG path for elevation line
  // If maxDist extends beyond the last track point (e.g., finish line beyond last waypoint),
  // extend the line flat from the last track point to the finish
  const lastPoint = points[points.length - 1]!;
  const trackEndsDist = lastPoint.distance;
  let linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(p.distance).toFixed(1)} ${toY(p.altitude).toFixed(1)}`)
    .join(' ');
  if (maxDist > trackEndsDist * 1.01) {
    // Extend line to the finish at the same altitude as the last track point
    linePath += ` L ${toX(maxDist).toFixed(1)} ${toY(lastPoint.altitude).toFixed(1)}`;
  }

  // Build fill path (close at bottom)
  const fillEndX = maxDist > trackEndsDist * 1.01 ? toX(maxDist) : toX(trackEndsDist);
  const fillPath = `${linePath} L ${fillEndX.toFixed(1)} ${svgPadding.top + chartHeight} L ${svgPadding.left} ${svgPadding.top + chartHeight} Z`;

  // Visible stations (parent multi-visit roots filtered out) with computed positions.
  // Reused for station markers, at-station bubbles, and midpoint en-route bubbles.
  const visibleStations = stations
    .filter((s) => !(isHiddenParentStation(s)))
    .map((station) => {
      let trackDist: number;
      if (station.check_mode === 'start') trackDist = 0;
      else if (station.check_mode === 'finish') trackDist = maxDist;
      else trackDist = stationTrackDistances.get(station.id) ?? station.mileage;
      const x = toX(trackDist);
      const y = toY(interpolateAltitude(points, trackDist));
      return { station, x, y, trackDist };
    });

  return (
    <div className={`v2-elevation ${mini ? 'v2-elevation-mini' : ''}`}>
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${svgHeight}`}
        preserveAspectRatio="none"
        style={{ height: svgHeight }}
      >
        {/* Fill area */}
        <path
          d={fillPath}
          fill="var(--v2-elevation-fill)"
        />

        {/* Elevation line */}
        <path
          d={linePath}
          fill="none"
          stroke="var(--v2-elevation-line)"
          strokeWidth="2"
        />

        {/* Aid station markers (dashed line, dot, label) */}
        {!mini && visibleStations.map(({ station, x, y }, i) => (
          <g
            key={station.id}
            onClick={() => onStationClick?.(i)}
            style={{ cursor: onStationClick ? 'pointer' : 'default' }}
          >
            <line
              x1={x}
              y1={y}
              x2={x}
              y2={svgPadding.top + chartHeight}
              stroke="var(--v2-border-dark)"
              strokeWidth="1"
              strokeDasharray="3,3"
              opacity="0.5"
            />
            <circle
              cx={x}
              cy={y}
              r="4"
              fill="var(--v2-elevation-line)"
              stroke="var(--v2-bg-page)"
              strokeWidth="2"
            />
            <text
              x={x}
              y={svgPadding.top + chartHeight + 14}
              textAnchor="middle"
              fill="var(--v2-text-muted)"
              fontSize="8"
              fontFamily="Inter"
            >
              {station.name.length > 12 ? station.name.slice(0, 10) + '...' : station.name}
            </text>
          </g>
        ))}

        {/* En-route bubbles: positioned at the midpoint x between this station and the prior one,
            with y anchored to the actual elevation curve at that point so the bubble sits
            above the line rather than potentially intersecting it.
            Counts runners with phase='enroute' whose next checkpoint is this station. */}
        {!mini && visibleStations.map(({ station, trackDist }, i) => {
          if (i === 0) return null; // no prior station
          const split = stationSplit.get(station.id);
          const enrouteRunners = split?.enrouteTo ?? [];
          if (enrouteRunners.length === 0) return null;
          const prior = visibleStations[i - 1]!;
          const midTrackDist = (prior.trackDist + trackDist) / 2;
          const midX = toX(midTrackDist);
          const midY = toY(interpolateAltitude(points, midTrackDist));
          return (
            <StationBubble
              key={`enroute-${station.id}`}
              cx={midX}
              cy={midY}
              count={enrouteRunners.length}
              kind="enroute"
              stationName={station.name}
              fromStationName={prior.station.name}
              runners={enrouteRunners}
              followedBibs={followedBibs ?? new Set()}
              onToggleFollow={onToggleFollow ?? (() => {})}
            />
          );
        })}

        {/* At-station bubbles: positioned on the station, runners checked in but not out */}
        {!mini && visibleStations.map(({ station, x, y }) => {
          const split = stationSplit.get(station.id);
          const atRunners = split?.atStation ?? [];
          if (atRunners.length === 0) return null;
          return (
            <StationBubble
              key={`at-${station.id}`}
              cx={x}
              cy={y}
              count={atRunners.length}
              kind="at_station"
              stationName={station.name}
              runners={atRunners}
              followedBibs={followedBibs ?? new Set()}
              onToggleFollow={onToggleFollow ?? (() => {})}
            />
          );
        })}

        {/* Runner position pin */}
        {runnerDistance != null && (() => {
          // Map official mileage to track distance using station calibration points
          const matchingStation = stations.find(s => Math.abs(s.mileage - runnerDistance) < 0.1);
          const trackDist = matchingStation
            ? (stationTrackDistances.get(matchingStation.id) ?? runnerDistance)
            : runnerDistance;
          return (
            <g>
              <line
                x1={toX(trackDist)}
                y1={svgPadding.top}
                x2={toX(trackDist)}
                y2={svgPadding.top + chartHeight}
                stroke="var(--v2-accent-secondary)"
                strokeWidth="2"
                strokeDasharray="4,4"
              />
              <circle
                cx={toX(trackDist)}
                cy={toY(interpolateAltitude(points, trackDist))}
                r="6"
                fill="var(--v2-accent-secondary)"
                stroke="#FFFFFF"
                strokeWidth="2"
              />
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// Minimal shape this helper needs — matches the `adapted.trackPoints` entries
// built by the adapter above (renamed from the stored profile's d/a keys).
// The old ElevationPoint interface also carried lat/lon, but this function
// never read them, so the narrower type is a pure type-only change.
type TrackPoint = { distance: number; altitude: number };

function interpolateAltitude(points: TrackPoint[], distance: number): number {
  if (points.length === 0) return 0;
  if (distance <= points[0]!.distance) return points[0]!.altitude;
  if (distance >= points[points.length - 1]!.distance) return points[points.length - 1]!.altitude;

  for (let i = 1; i < points.length; i++) {
    if (points[i]!.distance >= distance) {
      const prev = points[i - 1]!;
      const curr = points[i]!;
      const t = (distance - prev.distance) / (curr.distance - prev.distance);
      return prev.altitude + t * (curr.altitude - prev.altitude);
    }
  }
  return points[points.length - 1]!.altitude;
}
